// report-behavior-error.ts — 19BM Work-area A · behavior-error reporting helper.
//
// 背景: 19BL 闭环覆盖了 render/runtime/unhandled-rejection/api-error 四类，但组件内部
// try/catch 主动吞掉的"非致命"错误（regen merge / autosave commit / rollback / PDF parse 等）
// 从来没有上报通道——UI 进入"显示成功但后端/本地没真生效"的不可恢复分支，操作者完全不知道。
// 这就是陛下最初报的"组件行为有问题但报不上来"。
//
// 本模块是 behavior-error 的单一入口: 10 个 silent catch 点统一调 reportBehaviorError，
//   Session 4 (19BM-A §3.1 eval 收编): +4 处 eval-sampler/export 静默失败
//   (eval-pool-parse / eval-pool-schema / eval-import-json-parse / eval-import-shape)。
// 它做两件事:
//   1. 30s dedup (Set<string> + setTimeout TTL) —— 同 (viewPath, userAction, code) 组合
//      30s 内只上报一次，防 navigation/重试噪音淹没真信号 (error-observability-hub §3.3
//      已预警 navigation 噪音淹真信号)。
//   2. 调 recordEvent ({ type: 'behavior-error', ... }) —— recordEvent (idb.ts:681) 是
//      never-throw 的统一 chokepoint，behavior-error 已在 ERROR_EVENT_TYPES (Task 1)，所以
//      recordEvent 会自动 stamp errorId → IDB → D1 client_events → admin feed → agent
//      水位线 (19BL session 5c)。零新基建。
//
// ErrorDiagnosis.userAction 字段 (telemetry.ts:68) 在 19BL 定义了但全代码库无人填 —— 本模块
// 把它用起来，让 agent 看到 behavior-error 时能知道用户当时在做什么。

import { recordEvent } from '../storage/idb.js';

/** 30s dedup window. Matches the value documented in the work-area A spec. */
const DEDUP_WINDOW_MS = 30_000;

/**
 * In-memory dedup set. Key = `${viewPath}|${userAction}|${code}`.
 * Entries auto-expire after DEDUP_WINDOW_MS via setTimeout. Process-memory only —
 * a page refresh clears it, which is the desired behavior (a refresh is a fresh
 * user session; re-reporting a still-broken behavior after refresh is signal, not noise).
 */
const recentReports = new Set<string>();

export interface BehaviorErrorInput {
  /** URL pathname where the behavior failed (location.pathname). */
  viewPath: string;
  /** Human description of what the user did, e.g. "内容生成 confirm". Fills ErrorDiagnosis.userAction. */
  userAction: string;
  /** Stable code for grouping in the admin feed, e.g. 'regen-confirm'. */
  code: string;
  /** Optional summary override; defaults to `${userAction} 失败 (${code})`. */
  summary?: string;
}

/**
 * Report a non-fatal component behavior failure as a behavior-error event.
 *
 * Dedup'd per (viewPath, userAction, code) within a 30s window. Never throws —
 * telemetry must not break the app (mirrors recordEvent's own never-throw contract).
 */
export async function reportBehaviorError(input: BehaviorErrorInput): Promise<void> {
  const { viewPath, userAction, code, summary } = input;
  const dedupKey = `${viewPath}|${userAction}|${code}`;
  if (recentReports.has(dedupKey)) return;

  recentReports.add(dedupKey);
  // Clear the key after the window so a still-broken behavior can re-report later.
  // Fire-and-forget — don't block the caller on the timer. (Browser setTimeout returns
  // a number, not a NodeJS handle, so no .unref() available; in Node/test it just runs.)
  setTimeout(() => {
    recentReports.delete(dedupKey);
  }, DEDUP_WINDOW_MS);

  try {
    await recordEvent({
      type: 'behavior-error',
      summary: summary ?? `${userAction} 失败 (${code})`,
      viewPath,
      payload: {
        userAction,
        code,
        // errorId is stamped automatically by recordEvent (behavior-error ∈ ERROR_EVENT_TYPES).
      },
    });
  } catch {
    // Swallow — telemetry must never break the calling code path.
    // recordEvent itself is never-throw, but a future refactor or env quirk shouldn't
    // propagate. The behavior-error catch site calling us is already in a failure path;
    // we must not make it worse.
  }
}

/**
 * Test-only: clear the dedup set. Exported so unit tests can reset between cases
 * without waiting 30s. NOT for production use.
 */
export function __resetBehaviorErrorDedupForTests(): void {
  recentReports.clear();
}
